import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function RecruiterVerify() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const personal = user.personal_email;
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState(personal ? "" : user.email);
  const [showChange, setShowChange] = useState(personal);
  const [busy, setBusy] = useState(false);
  if (user.role !== "recruiter" || (user.email_verified && !personal)) return <Navigate to="/recruiter" replace />;
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const verify = () => run(async () => { try { await api.post("/auth/verify-code", { code }); } catch (e) { setCode(""); throw e; } await refresh(); toast.success("Company email verified"); nav("/recruiter"); });
  const resend = () => run(async () => { await api.post("/auth/resend-verification"); setCode(""); toast.success(`New code sent to ${user.email}`); });
  const change = () => run(async () => { await api.post("/auth/change-work-email", { email }); setSentTo(email.toLowerCase()); setCode(""); toast.success(`Code sent to ${email}`); });
  return (
    <div className="mx-auto max-w-lg" data-testid="recruiter-verify-page">
      <div className="overflow-hidden rounded-3xl border bg-white shadow-xl">
        <div className="bg-[linear-gradient(120deg,#0f766e,#0369a1)] p-6 text-white"><ShieldCheck className="h-8 w-8 text-amber-300" />
          <h1 className="mt-3 text-2xl font-extrabold">Verify your company email</h1>
          <p className="mt-1 text-sm text-sky-100">To protect candidates, every hiring account on JobPedestal is tied to a verified company or agency email.</p></div>
        <div className="space-y-5 p-6">
          {showChange && <div className="space-y-2" data-testid="change-email-section">
            <p className="text-sm">{personal ? <>Your account uses <b>{user.email}</b>, a personal address. Enter your company email and we'll send it a code.</> : "Enter the correct company email and we'll send it a new code."}</p>
            <div className="flex gap-2"><Input type="email" placeholder="you@yourcompany.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="change-email-input" />
              <Button disabled={busy || !email.includes("@")} onClick={change} data-testid="change-email-send">Send code</Button></div>
          </div>}
          {sentTo && <div className="space-y-3" data-testid="code-section">
            <p className="text-sm">Enter the 6-digit code sent to <b>{sentTo}</b>. It expires in 15 minutes.</p>
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="verify-code-input">
              <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
            </InputOTP>
            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full" disabled={busy || code.length !== 6} onClick={verify} data-testid="verify-code-submit">Verify</Button>
              {!personal && <Button variant="outline" className="rounded-full" disabled={busy} onClick={resend} data-testid="verify-code-resend">Send a new code</Button>}
              {!showChange && <Button variant="ghost" className="rounded-full" onClick={() => setShowChange(true)} data-testid="verify-wrong-email">Wrong email?</Button>}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}
